/**
 * Shared surface primitives (CD review M2). Typed React wrappers around the
 * existing `.surface` / `.panel` / `.kpi` / `.dtable` classes — so new code has a
 * consistent shape (fixed spacing, right-aligned numerics, the same scaffold per
 * surface) without retro-fitting all 20 existing surfaces.
 *
 * The classes themselves are styled in styles.css (M2 CSS polish adds zebra rows
 * + sticky/numeric headers + a hover that wins over zebra). Existing surfaces
 * already use the classes, so they inherit the CSS upgrades for free; this file
 * is the OPT-IN component API for new work.
 */

import type { ReactNode } from 'react';

export function Surface({ section, title, sub, children }: {
  section?: string; title: string; sub?: ReactNode; children: ReactNode;
}) {
  return (
    <div className="surface">
      <div className="surface__header">
        {section && <span className="surface__sec">{section}</span>}
        <h1 className="surface__title">{title}</h1>
      </div>
      {sub && <div className="surface__sub">{sub}</div>}
      {children}
    </div>
  );
}

export function Panel({ title, meta, accent = false, children }: {
  title: string; meta?: ReactNode; accent?: boolean; children: ReactNode;
}) {
  return (
    <div className={`panel${accent ? ' panel--accent' : ''}`}>
      <div className="panel__head">
        <span className="panel__title">{title}</span>
        {meta != null && <span className="panel__meta">{meta}</span>}
      </div>
      {children}
    </div>
  );
}

/** One KPI cell — label / value / sub. Use inside <KpiStrip>. */
export function Kpi({ label, value, sub, tone }: {
  label: string; value: ReactNode; sub?: ReactNode; tone?: 'accent' | 'signal' | 'danger';
}) {
  const cls = tone ? `kpi__value kpi__value--${tone}` : 'kpi__value';
  return (
    <div className="kpi">
      <div className="kpi__label">{label}</div>
      <div className={cls}>{value}</div>
      {sub != null && <div className="kpi__sub">{sub}</div>}
    </div>
  );
}

/** A row of Kpi cells — fixes the 4/5/6-per-surface drift CD flagged: keep ≤5. */
export function KpiStrip({ children }: { children: ReactNode }) {
  return <div className="kpis">{children}</div>;
}

/** Typed table — columns mark `num` for right-aligned mono cells (header + body)
 *  and `width` for column hints. The CSS handles zebra rows + hover + numerics. */
export interface DataColumn<T> {
  key: string;
  header: ReactNode;
  cell: (row: T) => ReactNode;
  /** Right-aligned + mono. */ num?: boolean;
  width?: string | number;
}

export function DataTable<T>({ columns, rows, rowKey, empty, scrollMaxHeight }: {
  columns: DataColumn<T>[];
  rows: readonly T[];
  rowKey: (row: T) => string | number;
  empty?: ReactNode;
  /** Wrap in a scroll container at this max height (lets headers stay visible). */
  scrollMaxHeight?: number | string;
}) {
  if (rows.length === 0) {
    return (
      <div style={{ padding: 'var(--bm-space-3)', fontSize: 'var(--bm-text-xs)', color: 'var(--bm-text-muted)' }}>
        {empty ?? 'No rows.'}
      </div>
    );
  }
  const table = (
    <table className="dtable">
      <colgroup>
        {columns.map(c => <col key={c.key} style={c.width ? { width: c.width } : undefined} />)}
      </colgroup>
      <thead>
        <tr>
          {columns.map(c => (
            <th key={c.key} className={c.num ? 'num' : undefined}>{c.header}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map(r => (
          <tr key={rowKey(r)}>
            {columns.map(c => (
              <td key={c.key} className={c.num ? 'num' : undefined}>{c.cell(r)}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
  return scrollMaxHeight != null
    ? <div style={{ overflow: 'auto', maxHeight: scrollMaxHeight }}>{table}</div>
    : table;
}
